import React, { useEffect, useMemo, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { useNavigate, useParams } from 'react-router-dom'
import { db } from '../firebase'

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday']

const normalizeText = (value) => String(value || '').trim()

const getCourseName = (course, fallback = 'Course') => {
  if (!course || typeof course !== 'object') return fallback
  return course.mcourseName || course.courseName || course.name || course.title || fallback
}

const normalizeDailyPlan = (days = [], fallbackTeachingAid = '', fallbackReference = '') => {
  if (!Array.isArray(days)) return []

  return days.map((day, index) => {
    if (typeof day === 'string') {
      return {
        id: `day-${index + 1}`,
        day: `Day ${index + 1}`,
        label: weekdays[index] || '',
        focus: day,
        teachingAid: fallbackTeachingAid,
        reference: fallbackReference,
      }
    }

    return {
      id: day.id || `day-${index + 1}`,
      day: day.day || `Day ${index + 1}`,
      label: day.label || day.weekday || day.date || weekdays[index] || '',
      focus: day.focus || day.topic || day.activities || day.description || '',
      teachingAid: day.teachingAid || fallbackTeachingAid,
      reference: day.reference || fallbackReference,
    }
  })
}

const normalizeScheme = (value) => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `week-${index + 1}`,
          week: String(index + 1),
          topic: item,
          objectives: '',
          activities: '',
          teachingAid: '',
          reference: '',
          dailyPlan: normalizeDailyPlan([]),
        }
      }

      return {
        id: item.id || `week-${index + 1}`,
        week: item.week || String(index + 1),
        topic: item.topic || '',
        objectives: item.objectives || item.objective || '',
        activities: item.activities || item.learningActivities || '',
        teachingAid: item.teachingAid || item.aid || '',
        reference: item.reference || '',
        dailyPlan: normalizeDailyPlan(
          item.dailyPlan || item.days || item.weekDays || item.dailyBreakdown,
          item.teachingAid || item.aid || '',
          item.reference || ''
        ),
      }
    })
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => ({
        id: `week-${index + 1}`,
        week: String(index + 1),
        topic: item,
        objectives: '',
        activities: '',
        teachingAid: '',
        reference: '',
        dailyPlan: normalizeDailyPlan([]),
      }))
  }

  return []
}

const makeEmptyDay = (index = 0) => ({
  id: `day-${Date.now()}-${index}`,
  day: `Day ${index + 1}`,
  label: weekdays[index] || '',
  focus: '',
  teachingAid: '',
  reference: '',
})

const makeEmptyWeek = (index = 0) => ({
  id: `week-${Date.now()}-${index}`,
  week: String(index + 1),
  topic: '',
  objectives: '',
  activities: '',
  teachingAid: '',
  reference: '',
  dailyPlan: weekdays.map((_, dayIndex) => makeEmptyDay(dayIndex)),
})

const sanitizeDraft = (weeks = []) => (
  weeks.map((week, weekIndex) => ({
    id: week.id || `week-${weekIndex + 1}`,
    week: normalizeText(week.week) || String(weekIndex + 1),
    topic: normalizeText(week.topic),
    objectives: normalizeText(week.objectives),
    activities: normalizeText(week.activities),
    teachingAid: normalizeText(week.teachingAid),
    reference: normalizeText(week.reference),
    dailyPlan: (Array.isArray(week.dailyPlan) ? week.dailyPlan : [])
      .map((day, dayIndex) => ({
        id: day.id || `day-${dayIndex + 1}`,
        day: normalizeText(day.day) || `Day ${dayIndex + 1}`,
        label: normalizeText(day.label) || weekdays[dayIndex] || '',
        focus: normalizeText(day.focus),
        teachingAid: normalizeText(day.teachingAid),
        reference: normalizeText(day.reference),
      }))
      .filter((day) => day.day || day.label || day.focus || day.teachingAid || day.reference),
  }))
)

function EditSchemeComp() {
  const auth = getAuth()
  const navigate = useNavigate()
  const { courseId } = useParams()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [course, setCourse] = useState(null)
  const [schemeDraft, setSchemeDraft] = useState([])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false)
        setErrorMessage('Please sign in to edit the scheme of work.')
        return
      }

      try {
        const courseRef = doc(db, 'courses', courseId)
        const courseSnap = await getDoc(courseRef)

        if (!courseSnap.exists()) {
          setErrorMessage('We could not find this course record to edit.')
          setCourse(null)
          setSchemeDraft([])
          return
        }

        const courseData = { id: courseSnap.id, ...courseSnap.data() }
        const normalizedScheme = normalizeScheme(
          courseData.schemeOfWork || courseData.scheme || courseData.courseScheme || courseData.schemeTemplate
        )

        setCourse(courseData)
        setSchemeDraft(normalizedScheme.length ? normalizedScheme : [makeEmptyWeek(0)])
        setErrorMessage('')
      } catch (error) {
        console.error('Error loading scheme editor:', error)
        setErrorMessage('Could not load the scheme editor right now.')
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth, courseId])

  const pageTitle = useMemo(() => getCourseName(course, 'Scheme of Work'), [course])

  const updateWeekField = (weekIndex, field, value) => {
    setSchemeDraft((current) => current.map((week, index) => (
      index === weekIndex ? { ...week, [field]: value } : week
    )))
  }

  const updateDayField = (weekIndex, dayIndex, field, value) => {
    setSchemeDraft((current) => current.map((week, index) => {
      if (index !== weekIndex) return week
      return {
        ...week,
        dailyPlan: (week.dailyPlan || []).map((day, rowIndex) => (
          rowIndex === dayIndex ? { ...day, [field]: value } : day
        )),
      }
    }))
  }

  const addWeek = () => {
    setSchemeDraft((current) => [...current, makeEmptyWeek(current.length)])
  }

  const removeWeek = (weekIndex) => {
    setSchemeDraft((current) => {
      if (current.length === 1) return current
      return current.filter((_, index) => index !== weekIndex)
    })
  }

  const addDayRow = (weekIndex) => {
    setSchemeDraft((current) => current.map((week, index) => {
      if (index !== weekIndex) return week
      const nextIndex = (week.dailyPlan || []).length
      return {
        ...week,
        dailyPlan: [...(week.dailyPlan || []), makeEmptyDay(nextIndex)],
      }
    }))
  }

  const removeDayRow = (weekIndex, dayIndex) => {
    setSchemeDraft((current) => current.map((week, index) => {
      if (index !== weekIndex) return week
      if ((week.dailyPlan || []).length === 1) return week
      return {
        ...week,
        dailyPlan: week.dailyPlan.filter((_, rowIndex) => rowIndex !== dayIndex),
      }
    }))
  }

  const saveScheme = async () => {
    if (!course?.id) return

    setSaving(true)
    setNotice('')
    setErrorMessage('')

    try {
      const sanitizedScheme = sanitizeDraft(schemeDraft)
      await updateDoc(doc(db, 'courses', course.id), {
        schemeOfWork: sanitizedScheme,
      })
      setSchemeDraft(sanitizedScheme)
      setNotice('Scheme of work updated successfully.')
    } catch (error) {
      console.error('Error saving scheme of work:', error)
      setErrorMessage('Could not save the scheme of work right now.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='editSchemePage'>
        <div className='editSchemeState'>
          <div className='spinner-border text-dark' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <p>Loading the scheme editor...</p>
        </div>
      </div>
    )
  }

  if (errorMessage && !course) {
    return (
      <div className='editSchemePage'>
        <div className='editSchemeState'>
          <h5>Scheme editor unavailable</h5>
          <p>{errorMessage}</p>
          <button type='button' className='editSchemeBack' onClick={() => navigate('/my-courses')}>
            Back to My Courses
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='editSchemePage'>
      <section className='editSchemeHero'>
        <div>
          <span className='instructorCoursesEyebrow'>Scheme editor</span>
          <h3>{pageTitle}</h3>
          <p>Edit the scheme week by week and adjust the daily plan row by row before saving it back to the course.</p>
        </div>

        <div className='editSchemeHeroActions'>
          <button type='button' className='editSchemeGhostButton' onClick={() => navigate('/my-courses')}>
            Back to My Courses
          </button>
          <button type='button' className='editSchemePrimaryButton' onClick={saveScheme} disabled={saving}>
            {saving ? 'Saving...' : 'Save Scheme'}
          </button>
        </div>
      </section>

      {notice ? <div className='editSchemeNotice success'>{notice}</div> : null}
      {errorMessage ? <div className='editSchemeNotice error'>{errorMessage}</div> : null}

      <div className='editSchemeToolbar'>
        <span>{schemeDraft.length} week{schemeDraft.length === 1 ? '' : 's'} in this scheme</span>
        <button type='button' className='editSchemeGhostButton' onClick={addWeek}>
          Add Week
        </button>
      </div>

      <div className='editSchemeList'>
        {schemeDraft.map((week, weekIndex) => (
          <article className='editSchemeWeekCard' key={week.id || `week-${weekIndex + 1}`}>
            <div className='editSchemeWeekTop'>
              <div className='editSchemeWeekBadge'>Week {week.week || weekIndex + 1}</div>
              <button
                type='button'
                className='editSchemeRemoveButton'
                onClick={() => removeWeek(weekIndex)}
                disabled={schemeDraft.length === 1}
              >
                Remove week
              </button>
            </div>

            <div className='editSchemeFormGrid'>
              <div className='editSchemeField editSchemeFieldSmall'>
                <label>Week Number</label>
                <input
                  type='text'
                  className='form-control editInput'
                  value={week.week}
                  onChange={(event) => updateWeekField(weekIndex, 'week', event.target.value)}
                />
              </div>

              <div className='editSchemeField'>
                <label>Topic</label>
                <input
                  type='text'
                  className='form-control editInput'
                  value={week.topic}
                  onChange={(event) => updateWeekField(weekIndex, 'topic', event.target.value)}
                />
              </div>

              <div className='editSchemeField editSchemeFieldWide'>
                <label>Objectives</label>
                <textarea
                  className='form-control editInput editTextarea'
                  rows='3'
                  value={week.objectives}
                  onChange={(event) => updateWeekField(weekIndex, 'objectives', event.target.value)}
                />
              </div>

              <div className='editSchemeField editSchemeFieldWide'>
                <label>Activities</label>
                <textarea
                  className='form-control editInput editTextarea'
                  rows='3'
                  value={week.activities}
                  onChange={(event) => updateWeekField(weekIndex, 'activities', event.target.value)}
                />
              </div>

              <div className='editSchemeField'>
                <label>Teaching Aid</label>
                <input
                  type='text'
                  className='form-control editInput'
                  value={week.teachingAid}
                  onChange={(event) => updateWeekField(weekIndex, 'teachingAid', event.target.value)}
                />
              </div>

              <div className='editSchemeField'>
                <label>Reference</label>
                <input
                  type='text'
                  className='form-control editInput'
                  value={week.reference}
                  onChange={(event) => updateWeekField(weekIndex, 'reference', event.target.value)}
                />
              </div>
            </div>

            <div className='editSchemeDailySection'>
              <div className='editSchemeDailyHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Daily plan</span>
                  <h6>Edit row by row</h6>
                </div>
                <button type='button' className='editSchemeGhostButton' onClick={() => addDayRow(weekIndex)}>
                  Add Day Row
                </button>
              </div>

              <div className='editSchemeTableWrap'>
                <table className='editSchemeTable'>
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Class Day</th>
                      <th>Daily Activity</th>
                      <th>Teaching Aid</th>
                      <th>Reference</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(week.dailyPlan || []).map((day, dayIndex) => (
                      <tr key={day.id || `${week.id}-${dayIndex}`}>
                        <td>
                          <input
                            type='text'
                            className='form-control editInput editSchemeCellInput'
                            value={day.day}
                            onChange={(event) => updateDayField(weekIndex, dayIndex, 'day', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control editInput editSchemeCellInput'
                            value={day.label}
                            onChange={(event) => updateDayField(weekIndex, dayIndex, 'label', event.target.value)}
                          />
                        </td>
                        <td>
                          <textarea
                            className='form-control editInput editTextarea editSchemeCellTextarea'
                            rows='3'
                            value={day.focus}
                            onChange={(event) => updateDayField(weekIndex, dayIndex, 'focus', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control editInput editSchemeCellInput'
                            value={day.teachingAid}
                            onChange={(event) => updateDayField(weekIndex, dayIndex, 'teachingAid', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control editInput editSchemeCellInput'
                            value={day.reference}
                            onChange={(event) => updateDayField(weekIndex, dayIndex, 'reference', event.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type='button'
                            className='editSchemeTableAction'
                            onClick={() => removeDayRow(weekIndex, dayIndex)}
                            disabled={(week.dailyPlan || []).length === 1}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}

export default EditSchemeComp
